import React from 'react';
import classNames from 'classnames';
import { ArrowRight, Star } from 'lucide-react';

import { useConfiguration } from '../../../context/configurationContext';
import { Button } from '../../../components/ui/button';
import { MarketplaceListingCard } from '../../../components/MarketplaceListingCard';

import { useFeaturedListings } from '../../../hooks/useFeaturedListings';
import { mapListingToCardProps } from '../../../util/listingCardAdapter';

const VISIBLE_COUNT = 8;

type FeaturedListingsSectionProps = {
  sectionId: string;
  className?: string;
  title?: { content?: string } | null;
  description?: { content?: string } | null;
  callToAction?: { fieldType?: string; href?: string; content?: string } | null;
  options?: any;
  allSections?: any[];
};

/**
 * Featured Hot Listings: pulls real listings from the shared
 * `featuredListings` redux pipeline. Section is hidden entirely when the
 * configured query returns nothing rather than rendering a mock fallback.
 */
const FeaturedListingsSection = (props: FeaturedListingsSectionProps) => {
  const { sectionId, className, title, description, callToAction, options, allSections } = props;
  const config = useConfiguration() as any;
  const categories = config?.categoryConfiguration?.categories || [];

  const { listings, fetched, inProgress } = useFeaturedListings(sectionId, options, allSections);

  const visibleListings = listings.slice(0, VISIBLE_COUNT);

  // Hide the section when the query has resolved with zero matches. Keeps the
  // landing page tight when the operator hasn't curated anything yet.
  if (fetched && !inProgress && visibleListings.length === 0) {
    return null;
  }

  const hasTitle = title?.content;
  const hasDescription = description?.content;

  return (
    <section
      id={sectionId}
      className={classNames('relative z-20 bg-background pb-16 pt-6', className)}
    >
      <div className="container mx-auto max-w-7xl px-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
          <div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-2 uppercase flex items-center gap-3">
              <Star className="h-8 w-8 text-yellow-500 fill-yellow-500" />
              {hasTitle ? title.content : 'Hot Listings'}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground uppercase tracking-[0.22em]">
              {hasDescription
                ? description.content
                : 'A fresh mix of nearby listings rotates automatically every hour.'}
            </p>
          </div>
          {callToAction?.href && (
            <Button
              asChild
              variant="ghost"
              className="font-heading uppercase tracking-wide w-full sm:w-auto"
              data-testid="button-hot-listings-view-all"
            >
              {callToAction.fieldType === 'externalButtonLink' ? (
                <a href={callToAction.href} target="_blank" rel="noopener noreferrer">
                  {callToAction.content || 'View All'} <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              ) : (
                <a href={callToAction.href}>
                  {callToAction.content || 'View All'} <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              )}
            </Button>
          )}
        </div>

        <div className="rounded-[28px] border border-border/80 bg-card p-4 shadow-[0_22px_70px_-34px_rgba(15,23,42,0.45)] md:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {visibleListings.map(listing => {
              const card = mapListingToCardProps(listing, { categories });
              return (
                <MarketplaceListingCard
                  key={card.id}
                  listingId={card.id}
                  listingSlug={card.slug}
                  title={card.title}
                  image={card.imgUrl}
                  preloadSrc={card.preloadSrc}
                  priceLabel={card.priceLabel}
                  condition={card.condition}
                  location={card.location}
                  category={card.category}
                  posted={card.posted}
                  sellerRating={card.reviewAvg}
                  sellerReviews={card.reviewCount}
                  membershipTier={card.membershipTier}
                  isVerified={card.isVerified}
                  badgeText="Hot"
                  badgeClassName="bg-yellow-400 text-yellow-900 hover:bg-yellow-400"
                  dataTestId={`card-featured-${card.id}`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedListingsSection;
